import { useEffect, useRef, useCallback } from "react";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Monitora inatividade do usuário e chama `onTimeout` quando o período
 * configurado (em minutos) expira sem interação.
 *
 * @param timeoutMinutes Minutos de inatividade antes do logout (0 = desabilitado)
 * @param onTimeout Callback executado quando o timeout expira
 * @param enabled Se false, o monitoramento não é ativado
 */
export function useInactivityTimeout(
  timeoutMinutes: number,
  onTimeout: () => void,
  enabled = true,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (timeoutMinutes > 0) {
      timerRef.current = setTimeout(
        () => onTimeoutRef.current(),
        timeoutMinutes * 60 * 1000,
      );
    }
  }, [timeoutMinutes]);

  useEffect(() => {
    if (!enabled || timeoutMinutes <= 0) return;

    resetTimer();

    const handleActivity = () => resetTimer();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [enabled, timeoutMinutes, resetTimer]);
}
